import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const UR = {
    fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', Arial, sans-serif",
    direction: 'rtl'
};

const fmt = (n) => {
    const num = parseFloat(n) || 0;
    if (num === 0) return '-';
    return num.toLocaleString('en-US');
};

const RecoveryDetailsP2 = ({ company, companyLanguage, sales, recoveries, ledgerParties, initialDate }) => {
    const navigate = useNavigate();

    // Header colors to match image/Roznamacha
    const HEADER_BG = '#7a9e7e';
    const TABLE_HEADER_BG = '#f0f4f0'; // Light greenish tint

    // Process data for the selected date
    const processedData = useMemo(() => {
        if (!initialDate) return [];

        // 1. Filter sales and recoveries by date
        const dateSales = sales.filter(s =>
            new Date(s.date).toLocaleDateString('en-CA') === initialDate
        );
        const dateRecoveries = recoveries.filter(r =>
            new Date(r.date).toLocaleDateString('en-CA') === initialDate
        );

        // 2. Group by customer
        const customerMap = new Map();

        // Add sales to map
        dateSales.forEach(s => {
            const partyId = (s.customerPartyId?._id || s.customerPartyId || 'walk-in').toString();
            const name = s.sellerNameUrdu || s.sellerName || 'نامعلوم';

            if (!customerMap.has(partyId)) {
                customerMap.set(partyId, { partyId, name, sale: 0, recovery: 0 });
            }
            const data = customerMap.get(partyId);
            data.sale += parseFloat(s.totalAmount || s.total) || 0;
        });

        // Add recoveries to map
        dateRecoveries.forEach(r => {
            const partyId = (r.customerPartyId?._id || r.customerPartyId || 'walk-in-rec').toString();
            const name = r.customerNameUrdu || r.customerName || 'نامعلوم';

            if (!customerMap.has(partyId)) {
                customerMap.set(partyId, { partyId, name, sale: 0, recovery: 0 });
            }
            const data = customerMap.get(partyId);
            data.recovery += parseFloat(r.recoveryAmount || r.amount) || 0;
        });

        // 3. Convert to array, add ledger data, and calculate balance
        return Array.from(customerMap.values()).map((item, idx) => {
            // Find current balance from ledger parties
            const ledgerParty = ledgerParties.find(p => p._id.toString() === item.partyId);
            const totalBalance = ledgerParty ? (ledgerParty.currentBalance || 0) : 0;
            const customerName = ledgerParty ? (ledgerParty.nameUrdu || ledgerParty.name) : item.name;

            return {
                ...item,
                name: customerName,
                sr: idx + 1,
                todayBalance: item.sale - item.recovery,
                totalBalance: totalBalance
            };
        });
    }, [sales, recoveries, ledgerParties, initialDate]);

    // Calculate totals
    const totals = useMemo(() => {
        return processedData.reduce((acc, curr) => ({
            sale: acc.sale + curr.sale,
            recovery: acc.recovery + curr.recovery,
            todayBalance: acc.todayBalance + curr.todayBalance,
            totalBalance: acc.totalBalance + curr.totalBalance
        }), { sale: 0, recovery: 0, todayBalance: 0, totalBalance: 0 });
    }, [processedData]);

    const displayDate = initialDate ? new Date(initialDate).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
    }) : '';

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
            {/* ── Page Header ── */}
            <div style={{ background: HEADER_BG, color: '#fff', textAlign: 'center', padding: '12px', position: 'relative' }}>
                <button
                    onClick={() => navigate(-1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded px-3 py-1 text-sm border border-white/30 transition-all"
                >
                    {companyLanguage === 'ur' ? 'واپس' : 'Back'}
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', ...UR }}>
                    {companyLanguage === 'ur' ? 'وصولی کی تفصیلات' : 'Recovery Details'}
                </h2>
                <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>
                    {displayDate}
                </div>
            </div>

            {/* ── Table Container ── */}
            <div className="overflow-x-auto p-4">
                <table className="w-full border-collapse" style={{ minWidth: '800px' }}>
                    <thead>
                        <tr style={{ background: TABLE_HEADER_BG }}>
                            {['نمبر شمار (Sr)', 'تاریخ (Date)', 'گاہک کا نام (Customer Name)', 'کل سیل (Sale Amount)', 'وصولی رقم (Recovery)', 'آج کا بیلنس (Today)', 'بقایا رقم (Balance)'].map((h, i) => (
                                <th key={i} className="border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700" style={{
                                    textAlign: i === 2 ? 'right' : 'center',
                                    ...(i >= 2 ? UR : {})
                                }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {processedData.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="border border-gray-200 py-10 text-center text-gray-500 italic">
                                    {companyLanguage === 'ur' ? 'اس تاریخ کے لئے کوئی ریکارڈ نہیں ملا' : 'No records found for this date'}
                                </td>
                            </tr>
                        ) : processedData.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                <td className="border border-gray-200 px-4 py-1.5 text-center text-sm">{row.sr}</td>
                                <td className="border border-gray-200 px-4 py-1.5 text-center text-sm">{initialDate}</td>
                                <td className="border border-gray-200 px-4 py-1.5 text-right text-sm font-semibold text-gray-800" style={UR}>
                                    {row.name}
                                </td>
                                <td className="border border-gray-200 px-4 py-1.5 text-center text-sm font-medium">
                                    {fmt(row.sale)}
                                </td>
                                <td className="border border-gray-200 px-4 py-1.5 text-center text-sm font-bold text-green-700">
                                    {fmt(row.recovery)}
                                </td>
                                <td className="border border-gray-200 px-4 py-1.5 text-center text-sm font-bold text-gray-700">
                                    {fmt(row.todayBalance)}
                                </td>
                                <td className="border border-gray-200 px-4 py-1.5 text-center text-sm font-bold text-orange-700">
                                    {fmt(row.totalBalance)}
                                </td>
                            </tr>
                        ))}

                        {/* Filler rows to match excel style if few records */}
                        {processedData.length > 0 && processedData.length < 6 && [...Array(6 - processedData.length)].map((_, i) => (
                            <tr key={`empty-${i}`}>
                                {[...Array(7)].map((__, j) => (
                                    <td key={j} className="border border-gray-200 px-4 py-1.5 h-8">&nbsp;</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>

                    {/* ── Totals Row ── */}
                    {processedData.length > 0 && (
                        <tfoot className="bg-gray-50">
                            <tr style={{ fontWeight: 'bold' }}>
                                <td colSpan={3} className="border border-gray-300 px-4 py-3 text-right text-lg" style={UR}>
                                    کل (Total)
                                </td>
                                <td className="border border-gray-300 px-4 py-3 text-center text-lg">
                                    {fmt(totals.sale)}
                                </td>
                                <td className="border border-gray-300 px-4 py-3 text-center text-lg text-green-700">
                                    {fmt(totals.recovery)}
                                </td>
                                <td className="border border-gray-300 px-4 py-3 text-center text-lg text-gray-700">
                                    {fmt(totals.todayBalance)}
                                </td>
                                <td className="border border-gray-300 px-4 py-3 text-center text-lg text-orange-700">
                                    {fmt(totals.totalBalance)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* ── Footer Print Button ── */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button
                    onClick={() => window.print()}
                    className="bg-slate-700 hover:bg-slate-800 text-white font-bold py-2 px-6 rounded shadow transition-all flex items-center gap-2"
                >
                    <span>🖨 پرنٹ کریں</span>
                </button>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
        @media print {
          nav, header, button, .footer, .DashboardHeader { display: none !important; }
          .bg-slate-50 { background: white !important; }
          .rounded-lg { border: none !important; box-shadow: none !important; }
          body { font-size: 10pt; }
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #999 !important; padding: 4px 6px !important; }
          th { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
          @page { margin: 1cm; }
        }
      `}} />
        </div>
    );
};

export default RecoveryDetailsP2;
